import React, { useState, useEffect } from 'react';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Typography, Alert, MenuItem, Select, FormControl,
  InputLabel, Chip, IconButton, List, ListItem, ListItemText, ListItemSecondaryAction, TablePagination
} from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon, PersonAdd as PersonAddIcon } from '@mui/icons-material';
import { projectService, userService, clientService } from '../services/api';
import { usePagination } from '../hooks/usePagination';

const ProjectManagement: React.FC = () => {
  const [projects, setProjects] = useState<any[]>([]);
  const [developers, setDevelopers] = useState<any[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<any>(null);
  const [assignedEmployees, setAssignedEmployees] = useState<any[]>([]);
  const [selectedDevs, setSelectedDevs] = useState<number[]>([]);
  const [message, setMessage] = useState('');
  const [managers, setManagers] = useState<any[]>([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    clientId: '',
    managerId: ''
  });

  useEffect(() => {
    loadProjects();
    loadDevelopers();
    loadClients();
    loadManagers();
  }, []);

  const loadProjects = async () => {
    try {
      const data = await projectService.getProjects();
      setProjects(data);
    } catch (error) {
      console.error('Error loading projects:', error);
    }
  };

  const loadDevelopers = async () => {
    try {
      const data = await userService.getEmployees();
      setDevelopers(data);
    } catch (error) {
      console.error('Error loading employees:', error);
    }
  };

  const loadClients = async () => {
    try {
      const data = await clientService.getClients();
      setClients(data);
    } catch (error) {
      console.error('Error loading clients:', error);
    }
  };

  const loadManagers = async () => {
    try {
      const data = await userService.getUsers();
      setManagers((data || []).filter((u: any) => u.role === 'Manager'));
    } catch (error) {
      console.error('Error loading managers:', error);
    }
  };

  const loadAssignedEmployees = async (projectId: number) => {
    try {
      const data = await projectService.getProjectEmployees(projectId);
      setAssignedEmployees(data);
    } catch (error) {
      console.error('Error loading assigned employees:', error);
    }
  };

  const handleCreate = async () => {
    try {
      const payload: any = {
        name: formData.name,
        description: formData.description,
        startDate: formData.startDate,
        endDate: formData.endDate || undefined,
        clientId: formData.clientId ? parseInt(formData.clientId) : undefined,
        managerId: formData.managerId ? parseInt(formData.managerId) : undefined
      };
      await projectService.createProject(payload);
      setMessage('Project created successfully');
      setOpen(false);
      loadProjects();
      setFormData({ name: '', description: '', startDate: new Date().toISOString().split('T')[0], endDate: '', clientId: '', managerId: '' });
      setTimeout(() => setMessage(''), 3000);
    } catch (error: any) {
      setMessage(error.response?.data?.message || 'Error creating project');
    }
  };

  const handleEditClick = (project: any) => {
    setSelectedProject(project);
    setFormData({
      name: project.name,
      description: project.description,
      startDate: project.startDate.split('T')[0],
      endDate: project.endDate ? project.endDate.split('T')[0] : '',
      clientId: project.clientId ? project.clientId.toString() : '',
      managerId: project.managerId ? project.managerId.toString() : ''
    });
    setEditOpen(true);
  };

  const handleUpdate = async () => {
    if (!selectedProject) return;
    try {
      const payload: any = {
        name: formData.name,
        description: formData.description,
        startDate: formData.startDate,
        endDate: formData.endDate || undefined,
        clientId: formData.clientId ? parseInt(formData.clientId) : null,
        // Backend treats 0 as "clear the manager" (ManagerId is nullable and only updated when
        // the field HasValue, so an explicit null would be ignored instead of clearing it).
        managerId: formData.managerId ? parseInt(formData.managerId) : 0
      };
      await projectService.updateProject(selectedProject.id, payload);
      setMessage('Project updated successfully');
      setEditOpen(false);
      loadProjects();
      setSelectedProject(null);
      setTimeout(() => setMessage(''), 3000);
    } catch (error: any) {
      setMessage(error.response?.data?.message || 'Error updating project');
    }
  };

  const handleDeleteClick = (project: any) => {
    setSelectedProject(project);
    setDeleteOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedProject) return;
    try {
      await projectService.deleteProject(selectedProject.id);
      setMessage('Project deleted successfully');
      setDeleteOpen(false);
      loadProjects();
      setSelectedProject(null);
      setTimeout(() => setMessage(''), 3000);
    } catch (error: any) {
      setMessage(error.response?.data?.message || 'Error deleting project');
    }
  };

  const handleAssignClick = async (project: any) => {
    setSelectedProject(project);
    await loadAssignedEmployees(project.id);
    setSelectedDevs([]);
    setAssignOpen(true);
  };

  const handleAssignMultiple = async () => {
    if (!selectedProject || selectedDevs.length === 0) return;
    try {
      for (const userId of selectedDevs) {
        await projectService.assignProject(userId, selectedProject.id);
      }
      setMessage(`${selectedDevs.length} employee(s) assigned successfully`);
      await loadAssignedEmployees(selectedProject.id);
      setSelectedDevs([]);
      setTimeout(() => setMessage(''), 3000);
    } catch (error: any) {
      setMessage(error.response?.data?.message || 'Error assigning employees');
    }
  };

  const handleUnassign = async (userId: number) => {
    if (!selectedProject) return;
    try {
      await projectService.unassignProject(userId, selectedProject.id);
      setMessage('Employee unassigned successfully');
      await loadAssignedEmployees(selectedProject.id);
      setTimeout(() => setMessage(''), 3000);
    } catch (error: any) {
      setMessage(error.response?.data?.message || 'Error unassigning employee');
    }
  };

  const filteredProjects = projects.filter(project => {
    const search = searchTerm.toLowerCase();
    return (
      project.name?.toLowerCase().includes(search) ||
      project.description?.toLowerCase().includes(search) ||
      project.clientName?.toLowerCase().includes(search)
    );
  });

  const availableDevelopers = developers.filter(
    dev => !assignedEmployees.some(emp => emp.id === dev.id)
  );
  const { pagedRows: pagedProjects, paginationProps: projectsPaginationProps } = usePagination(filteredProjects);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h6">Projects</Typography>
        <Button variant="contained" onClick={() => setOpen(true)}>
          Create Project
        </Button>
      </Box>

      <TextField
        fullWidth
        label="Search projects by name, description, or client"
        variant="outlined"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 2 }}
      />

      {message && <Alert severity="info" sx={{ mb: 2 }}>{message}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Client</TableCell>
              <TableCell>Manager</TableCell>
              <TableCell>Start Date</TableCell>
              <TableCell>End Date</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pagedProjects.map((project) => (
              <TableRow key={project.id}>
                <TableCell>{project.name}</TableCell>
                <TableCell>{project.description}</TableCell>
                <TableCell>{project.clientName || '-'}</TableCell>
                <TableCell>{project.managerName || '-'}</TableCell>
                <TableCell>{new Date(project.startDate).toLocaleDateString()}</TableCell>
                <TableCell>{project.endDate ? new Date(project.endDate).toLocaleDateString() : 'N/A'}</TableCell>
                <TableCell>
                  <IconButton size="small" color="primary" onClick={() => handleEditClick(project)} title="Edit">
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDeleteClick(project)} title="Delete">
                    <DeleteIcon />
                  </IconButton>
                  <IconButton size="small" color="success" onClick={() => handleAssignClick(project)} title="Assign Employees">
                    <PersonAddIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {filteredProjects.length > 0 && <TablePagination {...projectsPaginationProps} />}
      </TableContainer>

      {/* Create Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Project</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Project Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            margin="normal"
            multiline
            rows={3}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Client (Optional)</InputLabel>
            <Select
              value={formData.clientId}
              onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
              label="Client (Optional)"
            >
              <MenuItem value="">None</MenuItem>
              {clients.map((client) => (
                <MenuItem key={client.id} value={client.id}>
                  {client.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Project Manager (Optional)</InputLabel>
            <Select
              value={formData.managerId}
              onChange={(e) => setFormData({ ...formData, managerId: e.target.value })}
              label="Project Manager (Optional)"
            >
              <MenuItem value="">None</MenuItem>
              {managers.map((mgr) => (
                <MenuItem key={mgr.id} value={mgr.id}>
                  {mgr.fullName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Start Date"
            type="date"
            value={formData.startDate}
            onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
            margin="normal"
            InputLabelProps={{ shrink: true }}
            required
          />
          <TextField
            fullWidth
            label="End Date"
            type="date"
            value={formData.endDate}
            onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Project</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Project Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            margin="normal"
            multiline
            rows={3}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Client (Optional)</InputLabel>
            <Select
              value={formData.clientId}
              onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
              label="Client (Optional)"
            >
              <MenuItem value="">None</MenuItem>
              {clients.map((client) => (
                <MenuItem key={client.id} value={client.id}>
                  {client.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Project Manager (Optional)</InputLabel>
            <Select
              value={formData.managerId}
              onChange={(e) => setFormData({ ...formData, managerId: e.target.value })}
              label="Project Manager (Optional)"
            >
              <MenuItem value="">None</MenuItem>
              {managers.map((mgr) => (
                <MenuItem key={mgr.id} value={mgr.id}>
                  {mgr.fullName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Start Date"
            type="date"
            value={formData.startDate}
            onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
            margin="normal"
            InputLabelProps={{ shrink: true }}
            required
          />
          <TextField
            fullWidth
            label="End Date"
            type="date"
            value={formData.endDate}
            onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdate} variant="contained">Update</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Delete Project</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the project "{selectedProject?.name}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Assign Employees Dialog */}
      <Dialog open={assignOpen} onClose={() => setAssignOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Manage Project Assignments - {selectedProject?.name}</DialogTitle>
        <DialogContent>
          {selectedProject?.managerName && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Employees assigned here will automatically report to {selectedProject.managerName}.
            </Alert>
          )}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>Assigned Employees</Typography>
            {assignedEmployees.length === 0 ? (
              <Typography color="text.secondary" sx={{ my: 2 }}>No employees assigned yet</Typography>
            ) : (
              <List dense>
                {assignedEmployees.map((emp) => (
                  <ListItem key={emp.id}>
                    <ListItemText 
                      primary={emp.fullName} 
                      secondary={`${emp.username} - ${emp.email}`}
                    />
                    <ListItemSecondaryAction>
                      <IconButton edge="end" color="error" onClick={() => handleUnassign(emp.id)} title="Remove">
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>

          <Box>
            <Typography variant="subtitle2" gutterBottom>Assign New Employees</Typography>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Select Employees</InputLabel>
              <Select
                multiple
                value={selectedDevs}
                onChange={(e) => setSelectedDevs(e.target.value as number[])}
                label="Select Employees"
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as number[]).map((value) => {
                      const dev = developers.find(d => d.id === value);
                      return <Chip key={value} label={dev?.fullName} size="small" />;
                    })}
                  </Box>
                )}
              >
                {availableDevelopers.map((dev) => (
                  <MenuItem key={dev.id} value={dev.id}>
                    {dev.fullName} ({dev.username})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignOpen(false)}>Close</Button>
          <Button 
            onClick={handleAssignMultiple} 
            variant="contained" 
            disabled={selectedDevs.length === 0}
          >
            Assign Selected
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectManagement;
